import { computed } from 'vue';
import api from '@/api';
import convertVapidKey from 'convert-vapid-public-key';
import type { SnackbarParams } from '@/components/common/snackbar/use-snackbar';
import { WebPushSubscription } from '@/types';
import { useStore } from '@/utils/vue';

export const useWebPush = () => {
  const store = useStore();

  /** storeに登録されている購読情報の更新状態を取得する */
  const getPendingState = () => {
    return computed(() => store.state.webPush.isPending);
  };
  /** Web Pushに関する Storeを初期化する */
  const initializeWebPushStore = async () => {
    if (!canReceiveWebPushNotification()) {
      await store.dispatch('webPush/changePending', false);
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    const fetchedSubscription = subscription
      ? await api.fetchWebPushSubscription(subscription.endpoint)
      : undefined;

    // DBに登録されている購読情報がある場合はstoreに登録する
    if (fetchedSubscription) {
      await store.dispatch('webPush/insertSubscription', fetchedSubscription);
    }

    await store.dispatch('webPush/changePending', false);
  };

  /** storeに登録されている購読情報を取得する */
  const getSubscription = () => {
    return computed(() => store.state.webPush.subscription);
  };

  /** Web Push通知を受け取れるか */
  const canReceiveWebPushNotification = () => {
    if (!('serviceWorker' in navigator)) {
      return false;
    }
    if (!('Notification' in window)) {
      return false;
    }
    return Notification?.permission === 'granted';
  };

  /**
   * @title web push通知を受け取るための購読処理
   * @desc 前段では通知を拒否している場合や、すでに購読済みの場合returnしている
   * @desc web pushは ①通知の許可 → ②subscribe → ③ ②の情報をバックエンドにPOSTする の手順が必要
   * ここでは②③の処理を行う
   * */
  const subscribeToWebPush = async () => {
    if (!canReceiveWebPushNotification()) return;
    await store.dispatch('webPush/changePending', true);
    const swRegistration = await navigator.serviceWorker.ready;
    const subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: convertVapidKey(
        import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY,
      ),
    });

    try {
      await api.postWebPushSubscription({
        pushSubscriptionJson: subscription.toJSON(),
        enabledMentionNotificationBrowser: true,
        enabledOtherUsersActivityNotificationBrowser: true,
      });
      await store.dispatch('webPush/insertSubscription', subscription);
      return 'SUCCESS';
    } catch {
      subscription.unsubscribe();
      return 'FAILED';
    } finally {
      await store.dispatch('webPush/changePending', false);
    }
  };

  const seeSubscription = async (subscription: WebPushSubscription) => {
    await api.seeWebPushNotification(subscription);
  };

  /**
   * @title ホーム画面表示時のWeb Push許可リクエスト
   * @desc web pushの設定をユーザーが選択していない場合にのみ、ホーム画面で許可を求める
   * @desc WARN: Notification.requestPermission は本来ユーザーの操作によってのみ呼び出すべきであるため、非推奨な実装です。
   * 経緯: https://stockmarkteam.slack.com/archives/C05FHRWA342/p1709184364826779
   */
  const requestWebPushPermissionOnHomeVisit = async (
    createSnackbar: (params: SnackbarParams) => void,
  ) => {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const result = await subscribeToWebPush();
    if (!result) return;
    if (result === 'SUCCESS') {
      createSnackbar({
        message: 'Push通知が有効化されました',
        type: 'success',
      });
    } else {
      createSnackbar({
        message: 'Push通知の有効化に失敗しました',
        type: 'error',
      });
    }
  };

  /** Web Push初期化処理 */
  const initWebPushWithNotification = async (
    createSnackbar: (params: SnackbarParams) => void,
  ) => {
    await initializeWebPushStore();
    const subscription = getSubscription();
    if (canReceiveWebPushNotification()) {
      if (!subscription.value) {
        const result = await subscribeToWebPush();
        if (!result) return;
        if (result === 'SUCCESS') {
          createSnackbar({
            message: 'Push通知が有効化されました',
            type: 'success',
          });
        } else {
          createSnackbar({
            message: 'Push通知の有効化に失敗しました',
            type: 'error',
          });
        }
      } else {
        await seeSubscription(subscription.value);
      }
    }
  };

  return {
    canReceiveWebPushNotification,
    subscribeToWebPush,
    getPendingState,
    initializeWebPushStore,
    getSubscription,
    seeSubscription,
    initWebPushWithNotification,
    requestWebPushPermissionOnHomeVisit,
  };
};
